import { Container, Xpadding } from "layouts";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BiHomeAlt, BiSearch, BiArrowBack } from "react-icons/bi";
import { FiBookOpen } from "react-icons/fi";
import astronautReading from "assets/images/astronaut_reading.png";

const NotFound = () => {
	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				duration: 0.6,
				staggerChildren: 0.2,
			},
		},
	};

	const itemVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.5 },
		},
	};

	const floatingAnimation = {
		y: [0, -10, 0],
		transition: {
			duration: 3,
			repeat: Infinity,
			ease: "easeInOut",
		},
	};

	return (
		<Container>
			<div className="min-h-screen flex flex-col justify-between">
				<Xpadding className="min-h-screen flex flex-col justify-center items-center">
					<motion.div
						variants={containerVariants}
						initial="hidden"
						animate="visible"
						className="text-center max-w-4xl mx-auto px-4"
					>
						{/* 404 Number */}
						<motion.div
							variants={itemVariants}
							className="mb-8"
						>
							<h1 className="text-8xl md:text-9xl font-bold text-akgreener opacity-20 select-none">
								404
							</h1>
						</motion.div>

						{/* Illustration */}
						<motion.div
							variants={itemVariants}
							className="mb-8 relative"
						>
							<motion.img
								src={astronautReading}
								alt="Astronaut reading in space"
								className="w-32 h-32 sm:w-48 md:h-48 mx-auto object-contain"
								animate={floatingAnimation}
							/>
							{/* Decorative elements */}
							<div className="absolute top-1/4 left-1/4 w-4 h-4 bg-akgreen rounded-full opacity-60 animate-pulse" />
							<div className="absolute top-1/3 right-1/4 w-3 h-3 bg-akgreener rounded-full opacity-40 animate-ping" />
							<div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-akgreenest rounded-full opacity-80 animate-bounce" />
						</motion.div>

						{/* Main Content */}
						<motion.div
							variants={itemVariants}
							className="mb-8"
						>
							<h2 className="text-3xl md:text-4xl font-bold text-gray-600 mb-4">
								Oops! Page Not Found
							</h2>
							<p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
								It looks like this page has floated off into space! Don't worry,
								you can always find your way back to our amazing collection of
								books.
							</p>
						</motion.div>

						{/* Action Buttons */}
						<motion.div
							variants={itemVariants}
							className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8"
						>
							<Link
								to="/books"
								className="group flex items-center gap-2 bg-akgreener text-white px-6 py-3 rounded-full font-medium hover:bg-akgreenest transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
							>
								<FiBookOpen size={20} />
								Browse Books
								<motion.div
									initial={{ x: 0 }}
									animate={{ x: [0, 5, 0] }}
									transition={{ duration: 1.5, repeat: Infinity }}
									className="group-hover:translate-x-1 transition-transform"
								>
									→
								</motion.div>
							</Link>

							<Link
								to="/"
								className="flex items-center gap-2 border-2 border-akgreener text-akgreener px-6 py-3 rounded-full font-medium hover:bg-akgreener hover:text-white transition-all duration-300 transform hover:scale-105"
							>
								<BiHomeAlt size={20} />
								Go Home
							</Link>
						</motion.div>

						{/* Quick Navigation */}
						<motion.div
							variants={itemVariants}
							className="bg-gray-50 rounded-2xl p-6 max-w-md mx-auto"
						>
							<h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
								<BiSearch
									size={20}
									className="text-akgreener"
								/>
								Quick Navigation
							</h3>
							<div className="space-y-3">
								<Link
									to="/books"
									className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-akgreener hover:text-white transition-all duration-200 group"
								>
									<span className="font-medium">Explore Books</span>
									<BiArrowBack
										size={16}
										className="transform rotate-180 group-hover:translate-x-1 transition-transform"
									/>
								</Link>
								<Link
									to="/subscriptions"
									className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-akgreener hover:text-white transition-all duration-200 group"
								>
									<span className="font-medium">Subscription Plans</span>
									<BiArrowBack
										size={16}
										className="transform rotate-180 group-hover:translate-x-1 transition-transform"
									/>
								</Link>
								<Link
									to="/support"
									className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-akgreener hover:text-white transition-all duration-200 group"
								>
									<span className="font-medium">Get Help</span>
									<BiArrowBack
										size={16}
										className="transform rotate-180 group-hover:translate-x-1 transition-transform"
									/>
								</Link>
							</div>
						</motion.div>

						{/* Fun Fact */}
						<motion.div
							variants={itemVariants}
							className="mt-8 p-4 bg-gradient-to-r from-akgreener/10 to-akgreen/10 rounded-xl border border-akgreener/20"
						>
							<p className="text-sm text-gray-600 italic">
								💡 <strong className="font-bold">Did you know?</strong> The
								first 404 error was created in 1990 by Tim Berners-Lee when he
								was building the World Wide Web at CERN. Our astronaut friend
								here is just exploring the digital universe!
							</p>
						</motion.div>
					</motion.div>
				</Xpadding>
			</div>
		</Container>
	);
};

export default NotFound;
